import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { displayDateTime } from 'reducers/TimeReducer';
import { handleError } from 'reducers/ErrorReducer';
import { makeStyles } from '@material-ui/core/styles';

import Tooltip from 'components/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import {
  showReport,
  showAddVehicle,
  showEditVehicle,
  showAddAttachment,
} from 'reducers/DialogsReducer';
import QuickFilterSearch from '../components/QuickFilterSearch';
import { searchVehicles, removeVehicle } from 'reducers/SearchReducer';
import PrintsSearch2 from '../components/PrintsSearch2';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import Fab from '@material-ui/core/Fab';
import CheckIcon from '@material-ui/icons/Check';

import SearchHotKeys from 'Search/SearchHotKeys';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';
import XGrid3 from 'components/XGrid3';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import { Redirect } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  filters: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: theme.spacing(2),
    '& > div': {
      marginRight: theme.spacing(2),
    },
  },
  gridWrap: {
    width: '100%',
    boxSizing: 'border-box',
    height: 600,
  },
  filterWrap: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  filter: {
    width: '100%',
    marginBottom: '8px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    '& button': {
      marginLeft: theme.spacing(1),
    },
  },
  panel: {
    '& .MuiDataGridPanelFooter-root': {
      display: 'none',
    },
  },
}));

const bool = (params) => (params.value ? <CheckIcon /> : <span></span>);

const defaultColumns = [
  { field: 'VehicleID', headerName: 'Vehicle ID', width: 150, hide: false },
  { field: 'PlateNumber', headerName: 'Plate Number', width: 150, hide: false },
  { field: 'PlateState', headerName: 'Plate State', width: 150, hide: false },
  {
    field: 'PlateExpiration',
    headerName: 'Plate Expiration',
    width: 150,
    valueFormatter: (params) => displayDateTime(params.value),
    format: 'date',
    hide: false,
  },
  { field: 'VIN', headerName: 'VIN', width: 150, hide: false },
  { field: 'Year', headerName: 'Year', width: 150, hide: false },
  { field: 'Make', headerName: 'Make', width: 150, hide: false },
  { field: 'Model', headerName: 'Model', width: 150, hide: false },
  { field: 'Style', headerName: 'Style', width: 150, hide: false },
  { field: 'PrimaryColor', headerName: 'Primary Color', width: 150, hide: false },
  {
    field: 'IsWanted',
    headerName: 'Is Wanted',
    width: 100,
    format: 'bool',
    renderCell: bool,
    hide: false,
  },
  { field: 'Party', headerName: 'Party', width: 150, hide: false },
  {
    field: 'Created',
    headerName: 'Created',
    width: 120,
    valueFormatter: (params) => displayDateTime(params.value),
    format: 'date',
    hide: false,
  },
  { field: 'CreatedBy', headerName: 'Created By', width: 150, hide: false },
  {
    field: 'Updated',
    headerName: 'Updated',
    width: 120,
    valueFormatter: (params) => displayDateTime(params.value),
    format: 'date',
    hide: false,
  },
  { field: 'UpdatedBy', headerName: 'Updated By', width: 150, hide: false },
  {
    field: 'IsDeleted',
    headerName: 'Is Deleted',
    width: 100,
    format: 'bool',
    renderCell: bool,
    hide: false,
  },
  {
    field: 'Deleted',
    headerName: 'Deleted',
    width: 120,
    valueFormatter: (params) => displayDateTime(params.value),
    format: 'date',
    hide: false,
  },
  { field: 'DeletedBy', headerName: 'Deleted By', width: 150, hide: false },
  { field: 'DeletedIP', headerName: 'Deleted IP', width: 140, hide: false },
];

function Vehicle(props) {
  const classes = useStyles();
  const { dataUpdate } = props;
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selection, setSelection] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [tables, setTables] = useState(null);
  const [colsSeq, setColsSeq] = useState([]);
  const [vehicleColumns, setVehicleColumns] = useState(defaultColumns);
  const [kbdKey, setKbdKey] = useState(null);
  const kbdTimeoutRef = useRef(0);
  const permissions = getFullPermissions('globals', 'Vehicles');

  useEffect(() => {
    getVehicleData();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (kbdKey) handleKeyPress();
    kbdTimeoutRef.current = setTimeout(() => {
      setKbdKey(null);
    }, 500);
  }, [kbdKey]);

  useEffect(() => {
    if (
      dataUpdate &&
      (dataUpdate.type === 'add-vehicle' ||
        dataUpdate.type === 'vehicle-reg' ||
        dataUpdate.type === 'edit-vehicle')
    )
      getVehicleData();
    // eslint-disable-next-line
  }, [dataUpdate]);

  const handleKeyPress = () => {
    switch (kbdKey) {
      case 'F5':
        getVehicleData();
        break;
      case 'Ctrl+KeyP':
        viewDetails();
        break;
    }
  };

  const getVehicleData = async () => {
    setLoaded(false);
    searchVehicles()
      .then((result) => setRows(processData(result)))
      .catch(props.handleError)
      .finally(() => setLoaded(true));
  };

  const processData = (data) => {
    return data.map((row, idx) => {
      return {
        ...row,
        id: idx,
      };
    });
  };

  const addVehicle = () => {
    props.showAddVehicle();
  };

  const editVehicle = () => {
    let item = filteredRows.find((row) => row.id === selection);
    if (!item) return;
    props.showEditVehicle(item.ptsVehicleID);
  };

  const viewDetails = () => {
    let item = filteredRows.find((row) => row.id === selection);
    if (!item) return;
    props.showReport({
      id: 33,
      data: { ptsVehicleID: item.ptsVehicleID },
      title: 'Vehicle Details',
    });
  };

  const addAttachment = () => {
    const item = rows.find((row) => row.id === selection);
    const { ptsVehicleID } = item;
    props.showAddAttachment({ id: ptsVehicleID, type: 'Vehicle' });
  };

  const delVehicle = () => {
    if (!window.confirm('Are you sure you want to remove selected vehicle?')) return;
    const item = rows.find((row) => row.id === selection);
    const { ptsVehicleID } = item;
    props.showSpinner();
    removeVehicle(ptsVehicleID).then().catch(props.handleError).finally(props.hideSpinner);
  };

  if (!permissions.Read) return <Redirect to="/" />;

  return (
    <div>
      <h5>Vehicles History</h5>
      <div className={classes.filterWrap}>
        <div className={classes.filterWrap}>
          <QuickFilterSearch
            rows={rows}
            columns={vehicleColumns}
            setFilteredRows={setFilteredRows}
          />
        </div>
        <div className={classes.actions}>
          {permissions.Create && (
            <Fab onClick={addVehicle} size="small" color="secondary">
              <AddIcon />
            </Fab>
          )}
          <PrintsSearch2
            title="Vehicle Records"
            data={filteredRows}
            cols={vehicleColumns}
            kbdKey={kbdKey}
          />
          {selection !== null && (
            <>
              {permissions.Edit && (
                <Tooltip title="Edit Vehicle">
                  <Fab className="ml-2" size="small" color="secondary" onClick={editVehicle}>
                    <EditIcon />
                  </Fab>
                </Tooltip>
              )}
              <Tooltip title="Veiw details">
                <Fab className="ml-2" size="small" color="secondary" onClick={viewDetails}>
                  <InfoIcon />
                </Fab>
              </Tooltip>
              <Tooltip title="Add attachment">
                <Fab className="ml-2" color="secondary" size="small" onClick={addAttachment}>
                  <AttachFileIcon />
                </Fab>
              </Tooltip>
              {permissions.Delete && (
                <Tooltip title="Remove vehicle">
                  <Fab className="ml-2" color="secondary" size="small" onClick={delVehicle}>
                    <DeleteIcon />
                  </Fab>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>
      <XGrid3
        name="vehicles"
        columns={vehicleColumns}
        setColumns={setVehicleColumns}
        rows={filteredRows}
        loading={!loaded}
        setSelection={setSelection}
      />
      <SearchHotKeys setKey={setKbdKey} />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    dataUpdate: state.dataUpdate,
  };
};

export default connect(mapStateToProps, {
  handleError,
  showReport,
  showAddVehicle,
  showEditVehicle,
  showAddAttachment,
  showSpinner,
  hideSpinner,
})(Vehicle);
